/**
 * LegalAcceptance - Componente de aceite de termos para signup
 * Exibe checkbox com links para Politica de Privacidade e Termos de Uso
 */

import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tab,
  Tabs,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import PrivacyPolicyContent from './PrivacyPolicyContent';
import TermsOfServiceContent from './TermsOfServiceContent';
import useIsMobile from '../../hooks/useIsMobile';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

interface LegalAcceptanceProps {
  accepted: boolean;
  onChange: (accepted: boolean) => void;
  error?: string;
}

const LegalAcceptance: React.FC<LegalAcceptanceProps> = ({
  accepted,
  onChange,
  error,
}) => {
  const isMobile = useIsMobile();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const handleOpenTerms = (e: React.MouseEvent, tab: number) => {
    e.preventDefault();
    setActiveTab(tab);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  const handleAcceptFromDialog = () => {
    onChange(true);
    setDialogOpen(false);
  };

  return (
    <>
      <Box sx={{ mt: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={accepted}
              onChange={(e) => onChange(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body2" color={error ? 'error' : 'text.primary'}>
              Li e aceito os{' '}
              <Link
                href="/termos"
                onClick={(e) => handleOpenTerms(e, 1)}
                sx={{ fontWeight: 600 }}
              >
                Termos de Uso
              </Link>
              {' '}e a{' '}
              <Link
                href="/privacidade"
                onClick={(e) => handleOpenTerms(e, 0)}
                sx={{ fontWeight: 600 }}
              >
                Política de Privacidade
              </Link>
              {' '}*
            </Typography>
          }
        />
        {error && (
          <Typography variant="caption" color="error" sx={{ ml: 4, display: 'block' }}>
            {error}
          </Typography>
        )}
      </Box>

      {/* Legal Documents Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { maxHeight: '90vh' },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Documentos Legais
          </Typography>
          <IconButton
            aria-label="fechar"
            onClick={handleClose}
            sx={{ color: 'grey.500' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            aria-label="documentos legais"
          >
            <Tab label="Política de Privacidade" />
            <Tab label="Termos de Uso" />
          </Tabs>
        </Box>

        <DialogContent dividers sx={{ minHeight: 400 }}>
          <TabPanel value={activeTab} index={0}>
            <PrivacyPolicyContent />
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            <TermsOfServiceContent />
          </TabPanel>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleClose} color="inherit">
            Fechar
          </Button>
          {!accepted && (
            <Button
              onClick={handleAcceptFromDialog}
              variant="contained"
              color="primary"
            >
              Li e Aceito
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LegalAcceptance;
